import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { esFechaValida, esHoraValida } from '../utils/dateTimeValidation';
import { esNombreValido } from '../utils/nameValidation';
import { signUpUser } from '../assets/firebaseConfig';
import { formatCityCountry } from '../utils/citySearch';
import { useScrollReset } from '../utils/useScrollReset';
import { useCityAutocomplete } from '../utils/useCityAutocomplete';

const RegisterModal = ({ show, onClose, t, i18n }) => {
  const navigate = useNavigate();
  const currentLanguage = i18n.language;

  const [nombre, setNombre] = useState('');
  const [apellido, setApellido] = useState('');
  const [fecha, setFecha] = useState('');
  const [hora, setHora] = useState('');
  const [fechaVisible, setFechaVisible] = useState('');
  const [horaVisible, setHoraVisible] = useState('');
  const [sinHora, setSinHora] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Un solo campo para lugar de nacimiento: el país y las coordenadas salen de
  // la ciudad elegida, no de un paso previo.
  const cityAutocomplete = useCityAutocomplete();
  const pais = cityAutocomplete.selectedCountry || '';
  const ciudad = cityAutocomplete.selectedCity || '';
  const ciudadCoords =
    cityAutocomplete.selectedLat != null && cityAutocomplete.selectedLng != null
      ? { lat: cityAutocomplete.selectedLat, lng: cityAutocomplete.selectedLng }
      : null;

  const [errorFecha, setErrorFecha] = useState('');
  const [errorHora, setErrorHora] = useState('');
  const [errorNombre, setErrorNombre] = useState('');
  const [errorApellido, setErrorApellido] = useState('');
  const [error, setError] = useState('');

  const [formularioInvalido, setFormularioInvalido] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  // Reinicia todo el formulario al cerrar.
  const resetForm = () => {
    setNombre('');
    setApellido('');
    setFecha('');
    setHora('');
    setFechaVisible('');
    setHoraVisible('');
    setSinHora(false);
    setEmail('');
    setPassword('');
    cityAutocomplete.hydrate({ city: '' });
    setError('');
    setErrorFecha('');
    setErrorHora('');
    setErrorNombre('');
    setErrorApellido('');
    setIsLoading(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const ciudadListRef = useScrollReset(cityAutocomplete.searchText, cityAutocomplete.results);

  const validateFecha = (selectedDate) => {
    if (!selectedDate) {
      setErrorFecha('');
      return false;
    }
    const [year, month, day] = selectedDate.split('-');
    if (!esFechaValida(day, month, year)) {
      setErrorFecha(t('form.fecha_error_rango'));
      return false;
    }
    setErrorFecha('');
    return true;
  };

  const handleFechaChange = (e) => {
    let value = e.target.value;
    const formattedValue = value.replace(/\D/g, '');
    if (formattedValue.length > 2 && formattedValue.length <= 4) {
      value = `${formattedValue.slice(0, 2)}/${formattedValue.slice(2)}`;
    } else if (formattedValue.length > 4) {
      value = `${formattedValue.slice(0, 2)}/${formattedValue.slice(2, 4)}/${formattedValue.slice(4, 8)}`;
    } else {
      value = formattedValue;
    }
    setFechaVisible(value);
    if (formattedValue.length === 8) {
      const day = formattedValue.slice(0, 2);
      const month = formattedValue.slice(2, 4);
      const year = formattedValue.slice(4, 8);
      setFecha(`${year}-${month}-${day}`);
      validateFecha(`${year}-${month}-${day}`);
    } else {
      setFecha('');
      validateFecha('');
    }
  };

  const handleHoraChange = (e) => {
    let value = e.target.value;
    const formattedValue = value.replace(/[^0-9:]/g, '');
    if (formattedValue.length > 2 && formattedValue.indexOf(':') === -1) {
      value = `${formattedValue.slice(0, 2)}:${formattedValue.slice(2, 4)}`;
    } else {
      value = formattedValue.slice(0, 5);
    }
    setHoraVisible(value);
    setHora(value);
  };

  const handleSinHoraChange = (e) => {
    const isChecked = e.target.checked;
    setSinHora(isChecked);
    if (isChecked) {
      setHora('');
      setHoraVisible('');
    }
  };

  useEffect(() => {
    const fechaOk = !!fecha && (() => {
      const [year, month, day] = fecha.split('-');
      return esFechaValida(day, month, year);
    })();
    const horaOk = sinHora || esHoraValida(hora);
    setErrorHora(!sinHora && hora.length === 5 && !horaOk ? t('form.hora_error') : '');

    const nombreOk = esNombreValido(nombre);
    const apellidoOk = esNombreValido(apellido);
    setErrorNombre(nombre && !nombreOk ? t('form.nombre_error') : '');
    setErrorApellido(apellido && !apellidoOk ? t('form.apellido_error') : '');

    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const passwordOk = password.length >= 6;

    const camposCompletos =
      nombre && apellido && fecha && (sinHora || hora) && pais && ciudad && email && password;

    setFormularioInvalido(
      !fechaOk ||
        !horaOk ||
        !nombreOk ||
        !apellidoOk ||
        !emailOk ||
        !passwordOk ||
        !camposCompletos,
    );
  }, [nombre, apellido, fecha, hora, sinHora, email, password, pais, ciudad, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading || formularioInvalido) return;
    setError('');
    setIsLoading(true);
    try {
      const birthTime = sinHora ? '' : hora;
      await signUpUser(
        email,
        password,
        nombre,
        apellido,
        fecha,
        birthTime,
        pais,
        ciudad,
        ciudadCoords,
      );
      resetForm();
      onClose();
      navigate('/');
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        setError(t('registerModal.emailInUse'));
      } else if (err.code === 'auth/invalid-email') {
        setError(t('registerModal.invalidEmail'));
      } else if (err.code === 'auth/weak-password') {
        setError(t('registerModal.weakPassword'));
      } else {
        setError(t('registerModal.genericError'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <div
      className={`register-modal-overlay ${show ? 'open' : ''}`}
      onClick={handleOverlayClick}
    >
      <div className="register-modal-light" onClick={(e) => e.stopPropagation()}>
        <h2
          style={{
            fontFamily: 'Effra',
            fontWeight: 300,
            fontSize: '1.4rem',
            textAlign: 'center',
            color: 'var(--dark-grey)',
            margin: '0 0 0.25rem',
          }}
        >
          {t('registerModal.title')}
        </h2>
        <p
          style={{
            fontFamily: 'Effra',
            fontWeight: 300,
            fontSize: '0.85rem',
            color: '#808080',
            textAlign: 'center',
            margin: '0 0 1.25rem',
          }}
        >
          {t('registerModal.subtitle')}
        </p>

        <form style={{ display: 'flex', flexDirection: 'column', gap: '.75rem' }} onSubmit={handleSubmit}>
          <div>
            <input
              autoComplete="off"
              type="text"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              className="form-input"
              placeholder={t('form.nombre')}
              style={{ textAlign: 'start' }}
            />
            {errorNombre && <p style={{ color: 'red', fontSize: '.75rem' }}>{errorNombre}</p>}
          </div>
          <div>
            <input
              autoComplete="off"
              type="text"
              value={apellido}
              onChange={(e) => setApellido(e.target.value)}
              className="form-input"
              placeholder={t('form.apellido')}
              style={{ textAlign: 'start' }}
            />
            {errorApellido && <p style={{ color: 'red', fontSize: '.75rem' }}>{errorApellido}</p>}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
            <label className="calculation-form-label" style={{ fontSize: '.85rem' }}>
              {t('form.fecha_nacimiento')}
            </label>
            <input
              type="text"
              value={fechaVisible}
              onChange={handleFechaChange}
              className="form-input date-input"
              placeholder="DD/MM/YYYY"
              maxLength="10"
              style={{ color: 'grey', textAlign: 'center' }}
            />
            {errorFecha && <p style={{ color: 'red', fontSize: '.75rem' }}>{errorFecha}</p>}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
            <label className="custom-checkbox-container">
              <input type="checkbox" checked={sinHora} onChange={handleSinHoraChange} />
              <span className="custom-checkmark round"></span>
              <span className="checkbox-label">{t('registerModal.noTime')}</span>
            </label>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
            <label className="calculation-form-label" style={{ fontSize: '.85rem' }}>
              {t('form.hora_nacimiento')}
            </label>
            <input
              type="text"
              value={horaVisible}
              onChange={handleHoraChange}
              className="form-input date-input"
              placeholder="HH:MM"
              maxLength="5"
              disabled={sinHora}
              style={{ color: 'grey', textAlign: 'center', opacity: sinHora ? 0.2 : 1 }}
            />
            {errorHora && <p style={{ color: 'red', fontSize: '.75rem' }}>{errorHora}</p>}
          </div>

          {/* Ciudad de nacimiento: busca en todos los países a la vez */}
          <div className="popup-dropdown">
            <div
              className={`popup-dropdown-header ${cityAutocomplete.isOpen ? 'popup-dropdown-header-open' : ''}`}
              onClick={() => cityAutocomplete.setIsOpen(true)}
            >
              <input
                autoComplete="off"
                type="text"
                className="dropdown-search-input"
                placeholder={t('form.select_city_search')}
                value={cityAutocomplete.searchText}
                onChange={(e) => cityAutocomplete.onChangeText(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onFocus={cityAutocomplete.onFocus}
                onBlur={cityAutocomplete.onBlur}
              />
              <svg viewBox="0 0 24 24" fill="currentColor" className="dropdown-icon">
                <path d="M7 10l5 5 5-5z" />
              </svg>
            </div>
            {cityAutocomplete.isOpen && (
              <ul ref={ciudadListRef} className={`popup-dropdown-list ${cityAutocomplete.isOpen ? 'open' : ''}`} style={{ maxHeight: '15vh' }}>
                {cityAutocomplete.results.length > 0 ? (
                  cityAutocomplete.results.map((item) => (
                    <li
                      key={`${item.country}-${item.value}-${item.lat}-${item.lng}`}
                      onClick={() => cityAutocomplete.onSelect(item)}
                    >
                      {formatCityCountry(item, currentLanguage)}
                    </li>
                  ))
                ) : cityAutocomplete.isLoading ? (
                  <li className="no-results">{t('loading')}</li>
                ) : (
                  <li className="no-results">{t('no_results')}</li>
                )}
              </ul>
            )}
          </div>

          <div>
            <input
              autoComplete="off"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="form-input"
              placeholder={t('loginPage.emailPlaceholder')}
              style={{ textAlign: 'start' }}
            />
          </div>
          <div>
            <input
              autoComplete="new-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="form-input"
              placeholder={t('loginPage.passwordPlaceholder')}
              style={{ textAlign: 'start' }}
            />
            <p style={{ color: '#808080', fontSize: '.7rem', margin: '.25rem 0 0' }}>
              {t('registerModal.passwordHint')}
            </p>
          </div>

          {error && <p style={{ color: 'red', fontSize: '.8rem', textAlign: 'center' }}>{error}</p>}

          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', margin: '10px auto 0' }}>
            <button type="button" onClick={handleClose} className="register-cancel-btn">
              {t('registerModal.cancel')}
            </button>
            <button
              disabled={formularioInvalido || isLoading}
              type="submit"
              className="register-submit-btn"
            >
              {isLoading ? t('registerModal.loading') : t('registerModal.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterModal;
